import { useState, useEffect } from 'react';
import {
  Table,
  ScrollArea,
  UnstyledButton,
  Group,
  Text,
  Center,
  TextInput,
  createStyles,
  Modal,
  rem,
  Button,
  Tooltip,
  Box,
} from '@mantine/core';
import { IconSelector, IconChevronDown, IconChevronUp, IconSearch, IconTrash, IconEditCircle, IconInfoCircle } from '@tabler/icons-react';
import usePagination from '../hooks/usePagination';
import Paginator from '../components/Paginator';
import { useDisclosure } from '@mantine/hooks';
import ContainerSubmissionEditForm from './forms/ContainerSubmissionEditForm';
import ConfirmModal from './ConfirmModal';

import { AppDataService } from '../services/AppDataService';
import moment from 'moment/moment';
import ReviewStatusIndicator from './ReviewStatusIndicator';

const useStyles = createStyles((theme) => ({
  tableContainer: {
    width: '100%',
    position: 'relative',
    overflow: 'hidden'
  },
  th: {
    padding: 0
  },
  control: {
    width: '100%',
    padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-md)',
    '&hover': {
      backgroundColor: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6));'
    }
  },
  icon: {
    width: rem(21),
    height: rem(21),
    borderRadius: rem(21)
  },
}));

const tableKeys = [
  'upc',
  'productName',
  'materialType',
  'size',
  'beverageType',
  'color',
  'reviewStatusName',
  'dateSubmitted',
  'dateModified'
];

function Th({ children, reversed, sorted, onSort }) {
  const { classes } = useStyles();
  const Icon = sorted ? (reversed ? IconChevronUp : IconChevronDown) : IconSelector;
  return (
    <th className={classes.th}>
      <UnstyledButton onClick={onSort} className={classes.control}>
        <Group justify="space-between" noWrap>
          <Text fw={500} fz="xs" miw={150}>
            {children}
          </Text>
          <Center className={classes.icon}>
            <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
          </Center>
        </Group>
      </UnstyledButton>
    </th>
  );
}

function filterData(data, search) {
  const query = search.toLowerCase().trim();
  return data.filter((item) =>
    tableKeys.some((key) => item[key].toLowerCase().includes(query))
  );
}

function sortData(data, payload) {
  const { sortBy } = payload;

  if (!sortBy) {
    return filterData(data, payload.search);
  }

  return filterData(
    [...data].sort((a, b) => {
      if (payload.reversed) {
        return b[sortBy].localeCompare(a[sortBy]);
      }

      return a[sortBy].localeCompare(b[sortBy]);
    }),
    payload.search
  );
}

export const ContainerRegistrationTable = ({ tableData, onEdit = () => {}, onDelete = () => {}, maxRows = 10, readonly = false }) => {
  const [search, setSearch] = useState('');
  const [sortedData, setSortedData] = useState(tableData);
  const [sortBy, setSortBy] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [editRowModalOpened, { open: openEditRowModal, close: closeEditRowModal }] = useDisclosure(false);
  const [activePage, pageCount, currentItems, onPageClick, setPageData] = usePagination(tableData, maxRows);

  useEffect(() => {
    setPageData(sortedData);
  }, [sortedData]);

  useEffect(() => {
    setSortedData(tableData);
  }, [tableData])

  const setSorting = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(tableData, { sortBy: field, reversed, search }));
  };

  const handleSearchChange = (event) => {
    const { value } = event.currentTarget;
    setSearch(value);
    setSortedData(sortData(tableData, { sortBy, reversed: reverseSortDirection, search: value }));
  };

  const handleDeleteRow = async (id) => {
    /*
    const registrationsFiltered = sortedData.filter(x => x.id !== id)

    await AppDataService.DeactivateUserContainerRegistration({ registrationId: id });

    setSortedData(registrationsFiltered)
    */
    onDelete(id);
  }

  const handleEditRow = (record) => {
    /*
    const registrationsFiltered = sortedData.filter(x => x.id !== record.id)
    registrationsFiltered.push(record);

    setSortedData(registrationsFiltered)
    */
    onEdit(record);
  }

  const handleClickEdit = (id) => {
    setSelectedRow(currentItems.find(x => x.id == id));
    openEditRowModal();
  }

  const rows = (currentItems || []).map((row, idx) => (
    <tr key={idx}>
      <td>
        {
          row.isDuplicate ? (
            <Group align='center' spacing='xs'>
              {row.upc}
              <Tooltip label="This is a duplicate UPC">
                <IconInfoCircle color='red' size={15} />
              </Tooltip>
            </Group>
          ) : (
            row.upc
          )
        }
      </td>
      <td>{row.productName}</td>
      <td>{row.materialType}</td>
      <td>{row.size}</td>
      <td>{row.beverageType}</td>
      <td>{row.color}</td>
      <td>
        <ReviewStatusIndicator
          reviewNotes={row.reviewNotes}
          reviewStatusDate={row.reviewStatusDate}
          reviewStatusName={row.reviewStatusName}
          reviewStatusId={row.reviewStatusId}
        />
      </td>
      <td>{moment(row.dateSubmitted).format('YYYY-MM-DD')}</td>
      <td>{moment(row.dateModified).format('YYYY-MM-DD')}</td>
      {
        (readonly === false) && (
          <td>
            <Group grow spacing='xs'>
              <Button compact color='dark' onClick={() => handleClickEdit(row.id)}><IconEditCircle size={15} /></Button>
              <ConfirmModal 
                title="Delete a submission"
                prompt="Are you sure you want to delete this submission?"
                confirmButtonText="Yes, delete it"
                confirmButtonColor="red"
                confirmButtonIcon={<IconTrash size="0.8rem" />}
                cancelButtonText="No, don't delete it"
                triggerModalButtonColor="red"
                triggerModalButtonText=""
                triggerModalButtonIcon={<IconTrash size={15} />}
                handleConfirm={() => handleDeleteRow(row.id)}
                handleCancel={() => {}}
              />
            </Group>
          </td>
        )
      }
    </tr>
  ));

  return (
    <Box>
      <Modal overlayProps={{ opacity: 0.3 }} size={1200} opened={editRowModalOpened} onClose={closeEditRowModal} title="Edit Submission" centered>
        <Center>
          <ContainerSubmissionEditForm
            onSubmit={(record) => handleEditRow(record)}
            submissionRecord={selectedRow}
            />
        </Center>
      </Modal>
      <TextInput
      placeholder="Search by any field"
      mb="md"
      icon={<IconSearch style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
      value={search}
      onChange={handleSearchChange}
      />
      <ScrollArea offsetScrollbars type='always' py='md' scrollbarSize={30}>
        <Table verticalSpacing="xs" layout="fixed" striped highlightOnHover withColumnBorders withBorder>
          <thead>
            <tr>
              <Th
                sorted={sortBy === 'upc'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('upc')}
              >
                UPC
              </Th>
              <Th
                sorted={sortBy === 'productName'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('productName')}
              >
                Product Brand & Name
              </Th>
              <Th
                sorted={sortBy === 'materialType'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('materialType')}
              >
                Material
              </Th>
              <Th
                sorted={sortBy === 'size'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('size')}
              >
                Size (oz)
              </Th>
              <Th
                sorted={sortBy === 'beverageType'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('beverageType')}
              >
                Beverage Type
              </Th>
              <Th
                sorted={sortBy === 'color'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('color')}
              >
                Color
              </Th>
              <Th
                sorted={sortBy === 'reviewStatusName'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('reviewStatusName')}
              >
                Review Status
              </Th>
              <Th
                sorted={sortBy === 'dateSubmitted'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('dateSubmitted')}
              >
                Date Submitted
              </Th>
              <Th
                sorted={sortBy === 'dateModified'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('dateModified')}
              >
                Date Modified
              </Th>
              {
                (readonly === false) && (<Th>Actions</Th>)
              }
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows
            ) : (
              <tr>
                <td colSpan={tableKeys.length}>
                  <Text fw={500} fz={20} ta="center" p={25}>
                    ...Nothing Found...
                  </Text>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </ScrollArea>
      <Paginator
        onPageClick={onPageClick}
        pageCount={pageCount}
        pageRangeDisplayed={5}
        marginPagesDisplayed={3}
        activePage={activePage}
      />
    </Box>
  );
}